import React, { useState, useEffect } from "react";
import { getRevenueSummary, getRevenueMonths, getGoal } from "../api/services";
import { useData } from "../hooks/useData";
import { KpiCard, Card, Loading } from "../components/common/UI";
import { MoneyBar, DoughnutChart } from "../components/charts/SimpleCharts";
import MonthlyRevenueTable from "../components/common/MonthlyRevenueTable";
import { fmtKop, fmtPct } from "../utils/format";
import styles from "./pages.module.css";

// Месяцы акселерации-года: Сводно + янв..окт 2026
const MONTHS = [
  { key: "all", label: "Сводно" },
  { key: "2026-01", label: "Янв" },
  { key: "2026-02", label: "Фев" },
  { key: "2026-03", label: "Мар" },
  { key: "2026-04", label: "Апр" },
  { key: "2026-05", label: "Май" },
  { key: "2026-06", label: "Июн" },
  { key: "2026-07", label: "Июл" },
  { key: "2026-08", label: "Авг" },
  { key: "2026-09", label: "Сен" },
  { key: "2026-10", label: "Окт" },
];

export default function Revenue() {
  const goal = useData(getGoal, []);
  const monthsTable = useData(getRevenueMonths, []);

  // текущий месяц по умолчанию (номер месяца 2026)
  const nowKey = `2026-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const [selected, setSelected] = useState(
    MONTHS.some((m) => m.key === nowKey) ? nowKey : "all"
  );

  const [prod, setProd] = useState([]);
  const [src, setSrc] = useState([]);
  const [totalKop, setTotalKop] = useState(0);
  const [loadingBreak, setLoadingBreak] = useState(true);

  useEffect(() => {
    let active = true;
    setLoadingBreak(true);
    Promise.all([
      getRevenueSummary("product", selected),
      getRevenueSummary("source", selected),
    ]).then(([p, s]) => {
      if (!active) return;
      setProd(p.data || []);
      setSrc(s.data || []);
      setTotalKop(p.total_kop || 0);
      setLoadingBreak(false);
    });
    const reload = () => {
      getRevenueSummary("product", selected).then((p) => { setProd(p.data || []); setTotalKop(p.total_kop || 0); });
      getRevenueSummary("source", selected).then((s) => setSrc(s.data || []));
    };
    window.addEventListener("ingest-done", reload);
    return () => { active = false; window.removeEventListener("ingest-done", reload); };
  }, [selected]);

  if (goal.loading) return <Loading />;
  const g = goal.data || {};
  const selectedLabel = MONTHS.find((m) => m.key === selected)?.label || "";

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Выручка и цель</h1>
          <div className={styles.pageSub}>Все источники сведены в рубли. Разбивка по месяцам.</div>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <KpiCard label="MRR этого месяца" value={fmtKop(g.current_mrr_kop, { short: true })} />
        <KpiCard label="Цель (месячный MRR)" value={fmtKop(g.target_mrr_kop, { short: true })} accent="var(--accent)" />
        <KpiCard label="Достигнуто" value={fmtPct(g.pct_to_goal)} />
      </div>

      {/* Переключатель месяцев */}
      <div className={styles.tabs} style={{ flexWrap: "wrap", alignSelf: "flex-start" }}>
        {MONTHS.map((m) => (
          <button
            key={m.key}
            className={`${styles.tab} ${selected === m.key ? styles.tabActive : ""}`}
            onClick={() => setSelected(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className={styles.grid2}>
        <Card title={`Выручка по продуктам — ${selectedLabel} (${fmtKop(totalKop, { short: true })})`}>
          {loadingBreak ? <Loading /> : prod.length ? (
            <MoneyBar labels={prod.map((p) => p.title)} values={prod.map((p) => p.gross_kop)} horizontal />
          ) : <Empty />}
        </Card>
        <Card title={`Выручка по источникам — ${selectedLabel}`}>
          {loadingBreak ? <Loading /> : src.length ? (
            <DoughnutChart labels={src.map((s) => s.title)} values={src.map((s) => s.gross_kop)} money />
          ) : <Empty />}
        </Card>
      </div>

      <Card title="Все месяцы по продуктам (2026, до октября)">
        {monthsTable.loading || !monthsTable.data ? (
          <Loading />
        ) : (
          <MonthlyRevenueTable
            products={monthsTable.data.products}
            months={monthsTable.data.months}
            onPickMonth={(mk) => {
              const key = mk.slice(0, 7);
              if (MONTHS.some((m) => m.key === key)) setSelected(key);
            }}
            selected={selected}
          />
        )}
      </Card>
    </div>
  );
}

function Empty() {
  return <div style={{ color: "var(--text-mute)", padding: 24 }}>Нет данных за этот период.</div>;
}
